/**
 * The barcode a printed label carries, and the symbology it must be drawn in (ATL-1264).
 *
 * WHY THESE TWO ARE RETURNED TOGETHER
 * -----------------------------------
 * `PrintStickersScreen` used to build the value in one place and pick the symbology in another,
 * and the second place had to guess what the first had done — from the first digit:
 *
 *     switch (barcode[0]) { case '2': return 'EAN13'; default: return 'UPC' }
 *
 * JsBarcode's UPC accepts exactly 12 digits, so every 13-digit EAN-13 rendered as NOTHING. That
 * is most real manufacturer codes — the Kazakhstan/Russia 46xx and 48xx prefixes, 72xx, all of
 * them. Measured against JsBarcode 3.12.3 with the reporting org's own catalog: 88 of their 147
 * products printed a label with a name, a price, and no barcode; 2,548 products across 18
 * organizations fleet-wide.
 *
 * The same split caused a second defect. The bulk-selection path appended the date+weight suffix
 * unconditionally while the one-at-a-time path guarded it with `length === 6`, so a 12-digit
 * merchant code printed when added singly and vanished when selected in bulk. One rule, two
 * copies, and the copies drifted.
 *
 * A twelve-digit string is genuinely ambiguous, which is why guessing cannot work: `210021` +
 * day `251` + weight `000` is an internal code that must be drawn as EAN13 (JsBarcode appends
 * the 13th check digit), while `745110854388` is a merchant's UPC-A. Nothing in the string
 * separates them — only knowing whether WE composed it does. So the composer, which knows,
 * returns both.
 *
 * WHY THERE IS ALWAYS A SYMBOLOGY
 * -------------------------------
 * A label with no barcode is worse than a label with an unusual one: it cannot be scanned at
 * all, and nothing on screen says so. Anything the strict symbologies reject — a mistyped check
 * digit, an alphanumeric supplier code, an unusual length — falls back to CODE128, which encodes
 * any ASCII. The sticker is then always scannable, and it scans back the digits that are on the
 * product card.
 */

/** The internal codes `generateBarcode()` mints: 20XXXX piece goods, 21XXXX weighted. */
const INTERNAL_CODE_LENGTH = 6

/**
 * The GS1 mod-10 check digit for a body of digits, weighting from the RIGHT.
 *
 * Right-anchored weighting is what makes one function correct for all three symbologies:
 * EAN-13's body is 12 digits weighted 1,3,1,3…, UPC-A's is 11 weighted 3,1,3,1…, and EAN-8's is
 * 7 weighted 3,1,3,1… — which are the same rule counted from the check digit inwards. Writing
 * it per-symbology is how the weights get transposed and a valid code is rejected as broken.
 */
const gs1CheckDigit = (body) => {
  let sum = 0
  for (let i = 0; i < body.length; i++) {
    // The rightmost body digit always carries weight 3.
    const weight = (body.length - 1 - i) % 2 === 0 ? 3 : 1
    sum += Number(body[i]) * weight
  }
  return (10 - (sum % 10)) % 10
}

/** Does a fully-formed code carry its own correct check digit? */
const hasValidCheckDigit = (code) =>
  gs1CheckDigit(code.slice(0, -1)) === Number(code[code.length - 1])

/**
 * The strictest symbology this value actually satisfies, or CODE128 when none do.
 *
 * Checked in the order a scanner cares about: a code that IS an EAN-13 must be drawn as one, so
 * the printed digits match the product card and the shelf label matches the packaging.
 */
const formatForMerchantCode = (value) => {
  if (!/^[0-9]+$/.test(value)) return 'CODE128'
  if (value.length === 13 && hasValidCheckDigit(value)) return 'EAN13'
  if (value.length === 12 && hasValidCheckDigit(value)) return 'UPC'
  if (value.length === 8 && hasValidCheckDigit(value)) return 'EAN8'
  return 'CODE128'
}

/**
 * What to print on the label for this product.
 *
 * @param {object} args
 * @param {string|number|null} args.barcode  the product's stored barcode; numeric in
 *   Firestore-read mode, a string from the API
 * @param {string} args.dayOfYear   three-digit day of year, `format(date, 'DDD')`
 * @param {string} [args.weightSegment='000']  three digits, hundredths of a unit; `'000'` means
 *   the label carries no weight yet
 * @returns {{value: string, format: string}|null} `null` when the product has no barcode at all
 *   — the caller must not print a label claiming to carry one.
 */
export const composeStickerBarcode = ({ barcode, dayOfYear, weightSegment = '000' }) => {
  const raw = barcode == null ? '' : String(barcode)
  // `0` is what validateProduct writes for "no barcode" (`parseInt(...) || 0`), so it is absence,
  // not a code.
  if (raw.length === 0 || raw === '0') return null

  // An internal code is the only one we extend, and the only one whose symbology we know
  // without inspecting it: six digits plus three plus three is twelve, which EAN13 completes
  // with a check digit of its own.
  if (raw.length === INTERNAL_CODE_LENGTH) {
    return { value: raw + dayOfYear + weightSegment, format: 'EAN13' }
  }

  return { value: raw, format: formatForMerchantCode(raw) }
}
